import React, { useState, useEffect } from 'react';

function getCountryName(countryCode) {
  const countryInfo = window.Country?.INFO_MAP?.[countryCode];
  return countryInfo?.name || countryCode;
}

function SearchForm({
  initialQuery = '',
  initialFilters,
  currentFilters,
  filterOptions,
  onSubmit,
  layout = 'horizontal',
  rfqTitle = null,
  placeholder = null,
}) {
  const [query, setQuery] = useState(initialQuery);
  const [filters, setFilters] = useState({
    categories: initialFilters.categories
      ? initialFilters.categories.split(',')
      : [],
    country: initialFilters.country ? initialFilters.country.split(',') : [],
    services: initialFilters.services ? initialFilters.services.split(',') : [],
    moq_range: initialFilters.moq_range ? [initialFilters.moq_range] : [],
    specialties: initialFilters.specialties
      ? initialFilters.specialties.split(',')
      : [],
    partner_types: initialFilters.partner_types
      ? initialFilters.partner_types.split(',')
      : [],
  });

  // Sync filters when currentFilters changes (for search_only layout)
  useEffect(() => {
    if (currentFilters && layout === 'search_only') {
      setFilters({
        categories: currentFilters.categories
          ? currentFilters.categories.split(',')
          : [],
        country: currentFilters.country ? currentFilters.country.split(',') : [],
        services: currentFilters.services ? currentFilters.services.split(',') : [],
        moq_range: currentFilters.moq_range ? [currentFilters.moq_range] : [],
        specialties: currentFilters.specialties
          ? currentFilters.specialties.split(',')
          : [],
        partner_types: currentFilters.partner_types
          ? currentFilters.partner_types.split(',')
          : [],
      });
    }
  }, [currentFilters, layout]);

  // Sync query when initialQuery changes
  useEffect(() => {
    setQuery(initialQuery);
    setLastSubmittedQuery(initialQuery);
  }, [initialQuery]);
  const [expandedSections, setExpandedSections] = useState({
    categories: false,
    countries: false,
    services: false,
    certifications: false,
    partner_types: false,
  });

  // Auto-expand sections if filters are set on mount
  useEffect(() => {
    if (initialFilters.categories) {
      setExpandedSections((prev) => ({ ...prev, categories: true }));
    }
    if (initialFilters.country) {
      setExpandedSections((prev) => ({ ...prev, countries: true }));
    }
    if (initialFilters.services) {
      setExpandedSections((prev) => ({ ...prev, services: true }));
    }
    if (initialFilters.specialties) {
      setExpandedSections((prev) => ({ ...prev, certifications: true }));
    }
    if (initialFilters.partner_types) {
      setExpandedSections((prev) => ({ ...prev, partner_types: true }));
    }
  }, []);

  function toggleSection(section) {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }

  function handleCheckboxChange(filterType, value) {
    setFilters((prev) => {
      const currentValues = prev[filterType];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      const newFilters = {
        ...prev,
        [filterType]: newValues,
      };

      // Submit with current query (including unsubmitted changes) and new filters
      setTimeout(() => {
        setLastSubmittedQuery(query);
        onSubmit(query, {
          categories: newFilters.categories.join(','),
          country: newFilters.country.join(','),
          services: newFilters.services.join(','),
          moq_range: newFilters.moq_range.join(','),
          specialties: newFilters.specialties.join(','),
          partner_types: newFilters.partner_types.join(','),
        });
      }, 0);

      return newFilters;
    });
  }

  function handleRegionSelectAll(region, countries) {
    const countryCodes = countries.map(country => country.code);
    const allSelected = countryCodes.every(code => filters.country.includes(code));
    
    setFilters((prev) => {
      const currentValues = prev.country;
      const newValues = allSelected
        ? currentValues.filter(code => !countryCodes.includes(code))
        : [...new Set([...currentValues, ...countryCodes])];

      const newFilters = {
        ...prev,
        country: newValues,
      };

      // Submit with current query (including unsubmitted changes) and new filters
      setTimeout(() => {
        setLastSubmittedQuery(query);
        onSubmit(query, {
          categories: newFilters.categories.join(','),
          country: newFilters.country.join(','),
          services: newFilters.services.join(','),
          moq_range: newFilters.moq_range.join(','),
          specialties: newFilters.specialties.join(','),
          partner_types: newFilters.partner_types.join(','),
        });
      }, 0);

      return newFilters;
    });
  }

  // Track if search has been submitted to know when to enable the button
  const [lastSubmittedQuery, setLastSubmittedQuery] = useState(initialQuery);
  const hasUnsubmittedChanges = query !== lastSubmittedQuery;

  const handleSubmit = (e) => {
    e.preventDefault();
    setLastSubmittedQuery(query);
    onSubmit(query, {
      categories: filters.categories.join(','),
      country: filters.country.join(','),
      services: filters.services.join(','),
      moq_range: filters.moq_range.join(','),
      specialties: filters.specialties.join(','),
      partner_types: filters.partner_types.join(','),
    });
  };

  const handleSearchOnlySubmit = (e) => {
    e.preventDefault();
    setLastSubmittedQuery(query);
    // For search_only layout, use the current filters state
    onSubmit(query, {
      categories: filters.categories.join(','),
      country: filters.country.join(','),
      services: filters.services.join(','),
      moq_range: filters.moq_range.join(','),
      specialties: filters.specialties.join(','),
      partner_types: filters.partner_types.join(','),
    });
  };

  // Only render search input for search_only layout
  if (layout === 'search_only') {
    return (
      <form onSubmit={handleSearchOnlySubmit} style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <input
          type="text"
          className="input search-input"
          placeholder={rfqTitle ? `RFQ: ${rfqTitle}` : (placeholder || "Search by item, factory, material, and more")}
          value={rfqTitle ? `RFQ: ${rfqTitle}` : query}
          onChange={(e) => !rfqTitle && setQuery(e.target.value)}
          autoFocus
          style={{ marginRight: '12px' }}
          readOnly={!!rfqTitle}
        />
        <button
          type="submit"
          className="button is-primary is-rounded"
          disabled={!hasUnsubmittedChanges}
          style={{ height: '56px', flexShrink: 0 }}
        >
          Search
        </button>
      </form>
    );
  }

  // Only render filters for filters_only layout
  if (layout === 'filters_only') {
    return (
      <div className="is-flex is-flex-direction-column">
        {/* Categories Section */}
        <div className="filter-section mb-4">
          <div
            className="filter-header is-clickable"
            onClick={() => toggleSection('categories')}
          >
            <span className="label mb-2">Categories</span>
            <span className="icon">
              <i
                className={`ph-bold ph-caret-${
                  expandedSections.categories ? 'up' : 'down'
                }`}
              ></i>
            </span>
          </div>

          <div
            className={`filter-content ${
              expandedSections.categories && 'is-expanded'
            }`}
          >
            {filterOptions.categories
              ?.sort((a, b) => a.name.localeCompare(b.name))
              .map((category) => (
                <label key={category.name} className="checkbox mb-2">
                  <input
                    type="checkbox"
                    checked={filters.categories.includes(category.name)}
                    onChange={() =>
                      handleCheckboxChange('categories', category.name)
                    }
                    className="mr-2"
                  />
                  {category.name}
                </label>
              ))}
          </div>
        </div>

        {/* Location Section */}
        <div className="filter-section mb-4">
          <div
            className="filter-header is-clickable"
            onClick={() => toggleSection('countries')}
          >
            <span className="label mb-2">Location</span>
            <span className="icon">
              <i
                className={`ph-bold ph-caret-${
                  expandedSections.countries ? 'up' : 'down'
                }`}
              ></i>
            </span>
          </div>

          <div
            className={`filter-content ${
              expandedSections.countries && 'is-expanded'
            }`}
          >
            {Object.entries(filterOptions.countries || {}).map(([region, countries]) => {
              const countryCodes = countries.map(country => country.code);
              const allSelected = countryCodes.every(code => filters.country.includes(code));
              
              return (
                <div key={region} className="mb-3">
                  <p className="has-text-weight-semibold mb-1">{region === 'Central America' ? 'LatAm' : region}</p>
                  <a
                    href="#"
                    className="is-size-7 has-text-grey is-underlined mb-3 is-block"
                    onClick={(e) => {
                      e.preventDefault();
                      handleRegionSelectAll(region, countries);
                    }}
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </a>
                  {countries
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((country) => (
                      <label key={country.code} className="checkbox mb-2">
                        <input
                          type="checkbox"
                          checked={filters.country.includes(country.code)}
                          onChange={() =>
                            handleCheckboxChange('country', country.code)
                          }
                          className="mr-2"
                        />
                        {country.name}
                      </label>
                    ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Partner Types Section */}
        <div className="filter-section mb-4">
          <div
            className="filter-header is-clickable"
            onClick={() => toggleSection('partner_types')}
          >
            <span className="label mb-2">Partner Type</span>
            <span className="icon">
              <i
                className={`ph-bold ph-caret-${
                  expandedSections.partner_types ? 'up' : 'down'
                }`}
              ></i>
            </span>
          </div>

          <div
            className={`filter-content ${
              expandedSections.partner_types && 'is-expanded'
            }`}
          >
            {filterOptions.partner_types?.map((partnerType) => (
              <label key={partnerType.name} className="checkbox mb-2">
                <input
                  type="checkbox"
                  checked={filters.partner_types.includes(partnerType.name)}
                  onChange={() =>
                    handleCheckboxChange('partner_types', partnerType.name)
                  }
                  className="mr-2"
                />
                {partnerType.name}
              </label>
            ))}
          </div>
        </div>

        {/* Attributes Section */}
        <div className="filter-section mb-4">
          <div
            className="filter-header is-clickable"
            onClick={() => toggleSection('certifications')}
          >
            <span className="label mb-2">Attributes</span>
            <span className="icon">
              <i
                className={`ph-bold ph-caret-${
                  expandedSections.certifications ? 'up' : 'down'
                }`}
              ></i>
            </span>
          </div>

          <div
            className={`filter-content ${
              expandedSections.certifications && 'is-expanded'
            }`}
          >
            {filterOptions.specialties?.map((specialty) => (
              <label key={specialty.name} className="checkbox mb-2">
                <input
                  type="checkbox"
                  checked={filters.specialties.includes(specialty.name)}
                  onChange={() =>
                    handleCheckboxChange('specialties', specialty.name)
                  }
                  className="mr-2"
                />
                <img
                  src={specialty.icon}
                  alt={specialty.name}
                  className="icon is-small mr-2"
                  style={{ width: '16px', height: '16px' }}
                />
                {specialty.name}
              </label>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      className="searchForm"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(query, {
          categories: filters.categories.join(','),
          country: filters.country.join(','),
          services: filters.services.join(','),
          moq_range: filters.moq_range.join(','),
          specialties: filters.specialties.join(','),
          partner_types: filters.partner_types.join(','),
        });
      }}
    >
      <div
        className={
          layout === 'vertical'
            ? 'is-flex is-flex-direction-column'
            : 'searchFields'
        }
      >
        <div className="field">
          <div className="control has-icons-left">
            <input
              type="text"
              className="input"
              placeholder="Search suppliers..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <span className="icon is-left">
              <i className="ph-bold ph-magnifying-glass"></i>
            </span>
          </div>
        </div>

        {layout === 'vertical' && (
          <div className="is-divider" data-content="Filters"></div>
        )}

        <div
          className={`field ${layout === 'horizontal' ? 'searchFilter' : ''}`}
        >
          <label className="label">Categories</label>
          <div className="control">
            <select
              className="select is-fullwidth"
              value={filters.categories}
              onChange={(e) =>
                handleCheckboxChange('categories', e.target.value)
              }
              disabled={!filterOptions.categories?.length}
            >
              <option value="">All categories</option>
              {filterOptions.categories
                ?.sort((a, b) => a.name.localeCompare(b.name))
                .map((category) => (
                  <option key={category.name} value={category.name}>
                    {category.name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div
          className={`field ${
            layout === 'horizontal' ? 'searchFilter' : 'mt-4'
          }`}
        >
          <label className="label">Location</label>
          <div className="control">
            <select
              className="select is-fullwidth"
              value={filters.country}
              onChange={(e) => handleCheckboxChange('country', e.target.value)}
              disabled={!filterOptions.countries?.length}
            >
              <option value="">All locations</option>
              {filterOptions.countries
                ?.filter((country) => country.name?.trim())
                .map((country) => (
                  <option key={country.name} value={country.name}>
                    {getCountryName(country.name)}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {layout === 'vertical' && (
          <button type="submit" className="button is-primary is-fullwidth mt-5">
            <i className="ph-bold ph-magnifying-glass mr-2"></i> Search
          </button>
        )}
      </div>

      {layout === 'horizontal' && (
        <button
          type="submit"
          className="button is-primary is-rounded searchSubmit"
        >
          <i className="ph-bold ph-magnifying-glass mr-2"></i> Search
        </button>
      )}
    </form>
  );
}

export default SearchForm;
